import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../../app/modules/auth/services/auth.service';
import { UserService } from '../services/user.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SETTINGS } from '../configs/common.settings';
import { clear, getItem, setItem } from '../utils/safe-storage';
import { PermissionService } from '../services/permission.service';
import { RoleStorageService } from '../services/role-storage.service';
import { normalizeRoleName, ROLES } from '../configs/permissions.config';

@Injectable()
export class AuthGuard {
  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private permissionService: PermissionService,
    private roleStorage: RoleStorageService
  ) {}

  private storeUserRole(role: string): void {
    if (!role) {
      return;
    }
    this.roleStorage.setRole(role);
    this.permissionService.notifyPermissionsChanged();
    this.permissionService.loadEffectiveFromServer();
  }

  /** Query params from the full navigation URL and route tree (Flutter webview handoff). */
  private resolveQueryParams(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Record<string, string> {
    const merged: Record<string, string> = {};
    try {
      const search = state.url.includes('?')
        ? state.url.slice(state.url.indexOf('?'))
        : '';
      if (search) {
        new URLSearchParams(search).forEach((value, key) => {
          merged[key] = value;
        });
      }
    } catch {
      // ignore parse errors
    }
    let node: ActivatedRouteSnapshot | null = route;
    while (node) {
      Object.assign(merged, node.queryParams);
      node = node.firstChild;
    }
    return merged;
  }

  /** Mobile app WebView: tokens and role passed on the URL query string. */
  private applyWebViewHandoff(queryParams: Record<string, string>, stateUrl: string): void {
    const accessToken = queryParams['access_token'];
    if (!accessToken) {
      return;
    }
    const needsHandoff =
      stateUrl.includes('/app/job') || stateUrl.includes('/app/user');
    if (!needsHandoff) {
      return;
    }

    setItem(
      SETTINGS.ACCESS_TOKEN,
      this.authService['encryption'].encrypt(accessToken)
    );

    const userId = queryParams['user_id'];
    if (userId) {
      setItem(SETTINGS.LOGGED_IN_USER_ID, userId);
    }

    const userRole = queryParams['user_role'];
    if (userRole) {
      this.storeUserRole(userRole);
    }

    const refreshToken = queryParams['refresh_token'];
    if (refreshToken) {
      setItem(
        SETTINGS.REFRESH_TOKEN,
        this.authService['encryption'].encrypt(refreshToken)
      );
    }

    this.authService.setLoggedInUser(accessToken);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const queryParams = this.resolveQueryParams(route, state);
    this.applyWebViewHandoff(queryParams, state.url);

    if (!this.authService.isLoggedIn()) {
      clear();
      this.router.navigate(['/auth/login']);
      return false;
    }

    const storedRole = this.roleStorage.getRole();
    if (storedRole === ROLES.STAFF) {
      this.router.navigate(['/auth/access-denied']);
      return false;
    }
    if (storedRole) {
      return true;
    }

    const userId = getItem(SETTINGS.LOGGED_IN_USER_ID);
    if (!userId) {
      return true;
    }

    // Role missing from storage (e.g. older session) — load before child routes run.
    return this.userService.userDetailsById(userId).pipe(
      map((user: any) => {
        const roleName = user?.role?.name || '';
        this.storeUserRole(roleName);
        if (normalizeRoleName(roleName) === ROLES.STAFF) {
          this.router.navigate(['/auth/access-denied']);
          return false;
        }
        return true;
      }),
      catchError(() => {
        this.router.navigate(['/auth/login']);
        return of(false);
      })
    );
  }
}
